import React, { useEffect, useRef, useState } from 'react'
import { Text } from '../../elements/Text'
import { Toggle } from '../../elements/Toggle'
import { mq, useMediaQuery } from '../../utils'
import { IProps } from './index'
import cn from 'classnames'
import { useModeToggle, ModeOptions, modes, IModeOptions } from '../../providers/toggleProvider'
import { useIsomorphicLayoutEffect } from '../../hooks/useIsomorphicLayoutEffect'
import { BrandOptions, useBrand } from '../../providers/brandProvider'
import { ToggleMenu as ToggleMenuDefault } from './component'

export const testIds = {
  toggleMenu: 'toggle-menu',
  toggleMenuWrapper: 'toggle-menu-wrapper',
}

export const ToggleMenu = (props: IProps) => {
  const theme = useBrand()
  if (theme === BrandOptions.default) {
    return <ToggleMenuDefault {...props} />
  }
  return <ToggleMenuLegacy {...props} />
}

const ToggleMenuLegacy: React.FC<IProps> = ({ leftText, rightText }) => {
  const [isFixed, setIsFixed] = useState(false)
  const toggleContainerRef = useRef<HTMLDivElement>(null)

  const { currentMode, setMode, mode } = useModeToggle()

  const isDesktop = useMediaQuery(`(${mq.laptop})`)

  useIsomorphicLayoutEffect(() => {
    setMode(ModeOptions.customer)
    return () => setMode(null)
  }, [setMode])

  useEffect(() => {
    if (!isDesktop) return

    const toggleOffsetTop = toggleContainerRef.current?.offsetTop

    const onScroll = () => {
      if (toggleOffsetTop && window.pageYOffset > toggleOffsetTop) {
        return setIsFixed(true)
      }
      return setIsFixed(false)
    }

    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [isDesktop])

  const isRightActive = currentMode === ModeOptions.teams

  const handleOnChange = () => {
    setMode(isRightActive ? ModeOptions.customer : ModeOptions.teams)
  }

  const handleLabelOnClick = (mode: IModeOptions) => {
    if (currentMode === mode) return
    setMode(mode)
  }

  const toggleContainerClasses = cn('toggle-menu__container', {
    'toggle-menu__container--fixed': isFixed || !isDesktop,
    'toggle-menu__container--slide-in': isFixed,
  })

  const leftTextClasses = cn('toggle-menu__left-text', {
    'toggle-menu__left-text--active': !isRightActive,
  })

  const rightTextClasses = cn('toggle-menu__right-text', {
    'toggle-menu__right-text--active': isRightActive,
  })

  return (
    <>
      <div
        className={toggleContainerClasses}
        ref={toggleContainerRef}
        data-testid={testIds.toggleMenuWrapper}
      >
        <div className="toggle-menu" data-testid={testIds.toggleMenu}>
          <button
            className="toggle-menu__label-btn"
            onClick={() => handleLabelOnClick(ModeOptions.customer)}
          >
            <Text size="lg-eyebrow">
              <span className={leftTextClasses}>{leftText}</span>
            </Text>
          </button>
          <Toggle
            checked={isRightActive}
            onChange={handleOnChange}
            checkBgColor={modes.teams.toggleUncheckColor}
            uncheckBgColor={modes.customer.toggleUncheckColor}
          />
          <button
            className="toggle-menu__label-btn"
            onClick={() => handleLabelOnClick(ModeOptions.teams)}
          >
            <Text size="lg-eyebrow">
              <span className={rightTextClasses}>{rightText}</span>
            </Text>
          </button>
        </div>
      </div>

      <style jsx>{`
        .toggle-menu {
          &__left-text,
          &__right-text {
            color: ${mode.toggleUncheckColor};
            transition: color 400ms;
            &--active {
              color: ${mode.toggleCheckColor};
            }
          }
          &__container--fixed {
            z-index: ${isDesktop ? 'unset' : '1'};
          }
        }
      `}</style>

      <style jsx>
        {`
          .toggle-menu {
            display: flex;
            background: $white-legacy;
            box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.1);
            gap: 0.75rem;
            padding: 1rem 2rem;
            width: max-content;
            justify-content: center;
            items-align: center;
            border-radius: 60px;
            animation: toggle-menu-bounce 5s ease-in-out;
            animation-delay: 1s;
            &__label-btn {
              cursor: pointer;
            }
            &__container {
              display: flex;
              justify-content: flex-end;
              position: absolute;
              right: 0;
              left: 0;
              top: 0;
              margin: 20rem auto 0;
              &--fixed {
                max-width: none;
                position: fixed;
                top: 96px;
                margin-top: 0;
                .toggle-menu {
                  animation: none;
                  padding: 1rem 0;
                  width: 100%;
                  border-radius: 0;
                }
              }
              &--slide-in {
                animation: toggle-slide-in 200ms;
              }
            }
          }

          @media ('${mq.largePhone}') {
            .toggle-menu {
              &__left-text,
              &__right-text {
                font-size: 12px;
              }
            }
          }

          @media ('${mq.tablet}') {
            .toggle-menu {
              &__container--fixed {
                top: 80px;
              }
            }
          }

          @media ('${mq.laptop}') {
            .toggle-menu {
              &__container {
                padding: 0 3.25rem;
              }
            }
          }

          @media ('${mq.desktop}') {
            .toggle-menu {
              &__container {
                margin-top: 20rem;
                padding: 0 5rem;
                &--fixed {
                  top: 87px;
                  padding: 0;
                  margin-top: 0;
                }
              }
            }
          }

          @media ('${mq.desktopLg}') {
            .toggle-menu {
              &__container {
                margin-top: 15.5rem;
                padding: 0 0.5rem;
                max-width: $container-max-width;
                &--fixed {
                  max-width: none;
                  margin-top: 0;
                  top: 110px;
                  padding: 0;
                }
              }
            }
          }

          @media ('${mq.desktopXLg}') {
            .toggle-menu {
              &__container {
                padding: 0;
              }
            }
          }

          @keyframes toggle-menu-bounce {
            0%,
            12%,
            24%,
            100% {
              transform: translateY(0);
            }
            6%,
            18% {
              transform: translateY(10px);
            }
          }

          @keyframes toggle-slide-in {
            from {
              transform: translateY(-100%);
            }
            to {
              transform: translateY(0);
            }
          }
        `}
      </style>
    </>
  )
}
